import { useEffect, useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, Save, X, Zap, ToggleLeft, ToggleRight } from 'lucide-react';
import {
  automationApi, type AutomationRule, type AutomationEvent,
  type AutomationCondition, type AutomationAction, type RuleInput,
} from '../../api/automation';

const EVENT_LABEL: Record<AutomationEvent, string> = {
  'ticket.created': 'Тикет создан',
  'ticket.updated': 'Тикет обновлён',
  'message.added': 'Добавлено сообщение',
};

const FIELDS = [
  { value: 'priority', label: 'Приоритет', options: ['P1', 'P2', 'P3', 'P4'] },
  { value: 'status', label: 'Статус', options: ['new', 'open', 'pending_customer', 'on_hold', 'resolved', 'closed', 'cancelled'] },
  { value: 'support_line', label: 'Линия', options: [1, 2, 3, null] },
  { value: 'channel', label: 'Канал (web/email/tg/max)' },
  { value: 'subject', label: 'Тема (subject)' },
  { value: 'organization.contract_active', label: 'Договор активен (true/false)', options: [true, false] },
  { value: 'organization.name', label: 'Имя организации' },
  { value: 'requester_email', label: 'Email отправителя' },
  { value: 'requester_email_domain', label: 'Домен email отправителя' },
  { value: 'has_assignee', label: 'Есть агент (true/false)', options: [true, false] },
];

const OPS = [
  { value: 'eq', label: '=' },
  { value: 'ne', label: '≠' },
  { value: 'in', label: 'в списке' },
  { value: 'not_in', label: 'не в списке' },
  { value: 'contains', label: 'содержит' },
  { value: 'starts_with', label: 'начинается с' },
  { value: 'lt', label: '<' },
  { value: 'gt', label: '>' },
];

const ACTION_TYPES = [
  { value: 'set_status', label: 'Сменить статус', payloadHint: '{"value":"open"}' },
  { value: 'set_priority', label: 'Сменить приоритет', payloadHint: '{"value":"P1"}' },
  { value: 'set_assignee', label: 'Назначить агента', payloadHint: '{"value":"alex@devkz.ru"}' },
  { value: 'set_support_line', label: 'Сменить линию', payloadHint: '{"value":2}' },
  { value: 'add_internal_note', label: 'Внутренний комментарий', payloadHint: '{"body":"Авто: эскалация на L2"}' },
  { value: 'set_ai_autopilot', label: 'Включить/выключить AI-автопилот', payloadHint: '{"value":true}' },
];

export default function AutomationEditor() {
  const [items, setItems] = useState<AutomationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<AutomationRule | 'new' | null>(null);

  const load = async () => {
    setLoading(true);
    try { setItems(await automationApi.list()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
        <h2 className="font-bold text-white inline-flex items-center gap-2">
          <Zap size={16} className="text-yellow-400" /> Триггеры автоматизации
        </h2>
        <button onClick={() => setEditing('new')}
          className="text-xs flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded">
          <Plus size={12} /> Новое правило
        </button>
      </header>
      <div className="p-3">
        <p className="text-[11px] text-slate-500 mb-2">
          Условия (AND) проверяются по событию. Все действия одного правила применяются по порядку.
          Правила запускаются по возрастанию `priority`. «Stop on match» останавливает дальнейшую проверку.
        </p>
        {loading ? (
          <Loader2 className="animate-spin text-blue-400 mx-auto my-4" />
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-6">Правил пока нет</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="text-left py-2 px-3">Название</th>
                <th className="text-left py-2 px-3">Событие</th>
                <th className="text-left py-2 px-3">Усл / Действ</th>
                <th className="text-right py-2 px-3">Приоритет</th>
                <th className="text-right py-2 px-3">Сработало</th>
                <th className="text-center py-2 px-3">Вкл</th>
                <th className="text-right py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(r => (
                <tr key={r.id} className="border-t border-slate-800">
                  <td className="py-2 px-3 text-slate-200">
                    {r.name}
                    {r.description && <div className="text-[10px] text-slate-500">{r.description}</div>}
                  </td>
                  <td className="py-2 px-3 text-slate-400 text-xs">{EVENT_LABEL[r.event] || r.event}</td>
                  <td className="py-2 px-3 text-slate-400 text-xs">
                    {r.conditions.length} / {r.actions.length}
                  </td>
                  <td className="py-2 px-3 text-right text-slate-300 text-xs">{r.priority}</td>
                  <td className="py-2 px-3 text-right text-slate-300 text-xs">{r.matched_count}</td>
                  <td className="py-2 px-3 text-center">
                    {r.enabled
                      ? <ToggleRight className="text-emerald-400 inline" size={16} />
                      : <ToggleLeft className="text-slate-600 inline" size={16} />}
                  </td>
                  <td className="py-2 px-3 text-right whitespace-nowrap">
                    <button onClick={() => setEditing(r)} className="p-1 text-slate-500 hover:text-blue-400">
                      <Pencil size={12} />
                    </button>
                    <button onClick={async () => {
                      if (!window.confirm(`Удалить правило «${r.name}»?`)) return;
                      await automationApi.delete(r.id); load();
                    }} className="p-1 text-slate-500 hover:text-red-400">
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editing && (
        <RuleModal
          rule={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </section>
  );
}

function RuleModal({ rule, onClose, onSaved }:
  { rule: AutomationRule | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<RuleInput>({
    name: rule?.name || '',
    description: rule?.description || '',
    event: rule?.event || 'ticket.created',
    enabled: rule?.enabled ?? true,
    conditions: rule?.conditions || [],
    actions: rule?.actions || [],
    priority: rule?.priority ?? 100,
    stop_on_match: rule?.stop_on_match || false,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      if (rule) await automationApi.update(rule.id, form);
      else await automationApi.create(form);
      onSaved();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setBusy(false); }
  };

  const addCond = () => setForm(f => ({ ...f, conditions: [...f.conditions, { field: 'priority', op: 'eq', value: 'P1' }] }));
  const upCond = (i: number, patch: Partial<AutomationCondition>) =>
    setForm(f => ({ ...f, conditions: f.conditions.map((c, idx) => idx === i ? { ...c, ...patch } : c) }));
  const rmCond = (i: number) => setForm(f => ({ ...f, conditions: f.conditions.filter((_, idx) => idx !== i) }));

  const addAction = () => setForm(f => ({ ...f, actions: [...f.actions, { type: 'set_priority', payload: { value: 'P2' } }] }));
  const upAction = (i: number, patch: Partial<AutomationAction>) =>
    setForm(f => ({ ...f, actions: f.actions.map((a, idx) => idx === i ? { ...a, ...patch } : a) }));
  const rmAction = (i: number) => setForm(f => ({ ...f, actions: f.actions.filter((_, idx) => idx !== i) }));

  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 overflow-y-auto py-10" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-2xl mx-4 shadow-2xl"
           onClick={e => e.stopPropagation()}>
        <header className="p-4 border-b border-slate-800 flex justify-between">
          <h3 className="text-lg font-bold text-white">{rule ? 'Редактировать правило' : 'Новое правило'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </header>
        <form onSubmit={submit} className="p-4 space-y-3 max-h-[80vh] overflow-y-auto">
          <Lbl label="Название">
            <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white" />
          </Lbl>
          <Lbl label="Описание (опц.)">
            <input value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white" />
          </Lbl>
          <div className="grid grid-cols-2 gap-3">
            <Lbl label="Событие">
              <select value={form.event}
                onChange={e => setForm({ ...form, event: e.target.value as AutomationEvent })}
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white">
                <option value="ticket.created">Тикет создан</option>
                <option value="ticket.updated">Тикет обновлён</option>
                <option value="message.added">Добавлено сообщение</option>
              </select>
            </Lbl>
            <Lbl label="Приоритет правила (меньше = раньше)">
              <input type="number" min={0} max={1000} value={form.priority}
                onChange={e => setForm({ ...form, priority: parseInt(e.target.value || '100', 10) })}
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white" />
            </Lbl>
          </div>
          <div className="flex gap-4 items-center text-xs text-slate-300 pt-1">
            <label className="flex items-center gap-1.5">
              <input type="checkbox" checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} />
              Активно
            </label>
            <label className="flex items-center gap-1.5">
              <input type="checkbox" checked={form.stop_on_match} onChange={e => setForm({ ...form, stop_on_match: e.target.checked })} />
              Stop on match
            </label>
          </div>

          {/* Conditions */}
          <div className="border-t border-slate-800 pt-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold text-slate-300">Условия (все должны выполниться, AND)</span>
              <button type="button" onClick={addCond}
                className="text-[10px] flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-white px-2 py-0.5 rounded">
                <Plus size={10} /> Условие
              </button>
            </div>
            {form.conditions.length === 0 && (
              <div className="text-[11px] text-slate-600 italic py-1">Без условий — правило применяется ко всем событиям.</div>
            )}
            {form.conditions.map((c, i) => (
              <div key={i} className="flex gap-1 mb-1.5 items-center text-xs">
                <select value={c.field} onChange={e => upCond(i, { field: e.target.value })}
                  className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-white flex-1">
                  {FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
                <select value={c.op} onChange={e => upCond(i, { op: e.target.value })}
                  className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-white w-24">
                  {OPS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <input value={c.value as string ?? ''}
                  onChange={e => upCond(i, { value: e.target.value })}
                  placeholder="значение"
                  className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-white flex-1" />
                <button type="button" onClick={() => rmCond(i)} className="text-slate-500 hover:text-red-400 p-1">
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="border-t border-slate-800 pt-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold text-slate-300">Действия (выполняются по порядку)</span>
              <button type="button" onClick={addAction}
                className="text-[10px] flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-white px-2 py-0.5 rounded">
                <Plus size={10} /> Действие
              </button>
            </div>
            {form.actions.length === 0 && (
              <div className="text-[11px] text-red-400 italic py-1">Хотя бы одно действие требуется.</div>
            )}
            {form.actions.map((a, i) => {
              const meta = ACTION_TYPES.find(t => t.value === a.type);
              return (
                <div key={i} className="flex gap-1 mb-1.5 items-start text-xs">
                  <select value={a.type} onChange={e => upAction(i, { type: e.target.value })}
                    className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-white w-56">
                    {ACTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <textarea rows={1}
                    value={JSON.stringify(a.payload)}
                    onChange={e => {
                      try { upAction(i, { payload: JSON.parse(e.target.value) }); }
                      catch { upAction(i, { payload: a.payload }); }
                    }}
                    placeholder={meta?.payloadHint || '{"value":"..."}'}
                    className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-white font-mono text-[11px] flex-1" />
                  <button type="button" onClick={() => rmAction(i)} className="text-slate-500 hover:text-red-400 p-1">
                    <Trash2 size={11} />
                  </button>
                </div>
              );
            })}
          </div>

          {err && <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm">{err}</div>}
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-800 -mx-4 px-4 -mb-4 pb-4 mt-3">
            <button type="button" onClick={onClose}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded text-slate-300 text-sm">Отмена</button>
            <button type="submit" disabled={busy}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 rounded text-white text-sm font-bold inline-flex items-center gap-1.5">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              Сохранить
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Lbl({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</span>
      {children}
    </label>
  );
}
